import { useEffect, useMemo, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { messageService } from "@/services/message.service";
import {
  notifyUnreadMessagesChanged,
  useUnreadMessages,
} from "@/app/hooks/useUnreadMessages";
import type { UserMessage } from "@/types/message.types";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { toast } from "sonner";
import { Mail, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE_OPTIONS = ["10", "25", "50"];

export function MessagesSettings({ navigate }: { navigate: (page: string) => void }) {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, transparent)",
  } as const;
  const { user } = useAuth();
  const dateLocale = language === "de" ? "de-DE" : "en-US";
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(dateLocale),
    [dateLocale]
  );
  const isAdmin = (user?.role ?? "").toUpperCase() === "ADMIN";
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const { unreadCount, refreshUnreadMessages } = useUnreadMessages(true);

  const resolveTypeLabel = (type: UserMessage["type"]) => {
    switch (type) {
      case "SYSTEM":
        return t("account.messagesTypeSYSTEM");
      case "ADMIN":
        return t("account.messagesTypeADMIN");
      case "USER":
      default:
        return t("account.messagesTypeUSER");
    }
  };

  const formatSummary = (start: number, end: number, count: number) => {
    const template =
      t("account.messagesResultsSummary") ||
      "Showing {{start}}-{{end}} of {{count}} results";
    return template
      .replace(/\{\{start\}\}/g, numberFormatter.format(start))
      .replace(/\{\{end\}\}/g, numberFormatter.format(end))
      .replace(/\{\{count\}\}/g, numberFormatter.format(count));
  };

  useEffect(() => {
    let isMounted = true;
    const loadMessages = async () => {
      setIsLoading(true);
      try {
        const response = await messageService.getMessages({
          page: page - 1,
          size: pageSize,
        });
        if (!isMounted) return;
        if (response.totalPages > 0 && page > response.totalPages) {
          setPage(response.totalPages);
          return;
        }
        setMessages(response.items);
        setTotalPages(response.totalPages);
        setTotalItems(response.totalElements);
      } catch (error: any) {
        if (!isMounted) return;
        toast.error(error?.response?.data?.message ?? t("common.genericError"));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    void loadMessages();
    return () => {
      isMounted = false;
    };
  }, [page, pageSize, t]);

  const handlePageSizeChange = (value: string) => {
    const nextSize = Number(value);
    setPageSize(nextSize);
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleMarkRead = async (messageId: string) => {
    try {
      const updated = await messageService.markMessageRead(messageId);
      setMessages((prev) =>
        prev.map((entry) =>
          entry.id === messageId
            ? { ...entry, readAt: updated.readAt }
            : entry
        )
      );
      notifyUnreadMessagesChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    }
  };

  const handleMarkAllRead = async () => {
    setIsMarkingAll(true);
    const nowIso = new Date().toISOString();
    try {
      await messageService.markAllRead();
      setMessages((prev) =>
        prev.map((entry) =>
          entry.readAt ? entry : { ...entry, readAt: nowIso }
        )
      );
      notifyUnreadMessagesChanged();
      await refreshUnreadMessages();
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsMarkingAll(false);
    }
  };

  const rangeStart = totalItems > 0 ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd =
    totalItems > 0 ? Math.min(page * pageSize, totalItems) : 0;
  const handleAdminNavigate = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("admin-tab", "messages");
    }
    navigate("admin");
  };

  return (
    <div className="space-y-6">
      <Card className="stat-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
          <Mail className="w-5 h-5" style={iconStyle} />
          {t("account.messages")}
        </CardTitle>
        <CardDescription className="card-description-text" style={primaryTextStyle}>
          {t("account.messagesDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isAdmin && (
          <div
            className="flex flex-col gap-2 rounded-lg border border-border bg-input-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            style={{ backgroundColor: "var(--input-background)" }}
          >
            <div className="space-y-1">
              <p className="text-sm font-medium" style={primaryTextStyle}>
                {t("account.messagesAdminLink")}
              </p>
              <p className="text-xs" style={secondaryTextStyle}>
                {t("account.messagesAdminDescription")}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAdminNavigate}
              className="bg-background hover:bg-background dark:bg-background dark:hover:bg-background"
            >
              {t("account.messagesAdminCta")}
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label style={primaryTextStyle}>
                {t("account.messagesResultsPerPage")}
              </Label>
              <Select
                value={String(pageSize)}
                onValueChange={handlePageSizeChange}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder={String(pageSize)} />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllRead}
                disabled={isMarkingAll}
              >
                {t("account.messagesMarkAllRead")}
              </Button>
            )}
          </div>
          {totalItems > 0 && (
            <p className="text-xs" style={secondaryTextStyle}>
              {formatSummary(rangeStart, rangeEnd, totalItems)}
            </p>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm" style={secondaryTextStyle}>
            {t("common.loading")}
          </p>
        ) : messages.length === 0 ? (
          <p className="text-sm" style={secondaryTextStyle}>
            {t("account.messagesEmpty")}
          </p>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const senderLabel =
                message.createdBy || t("account.messagesFromSystem");
              return (
                <div
                  key={message.id}
                  className="rounded-lg border border-border p-4 space-y-3"
                >
                  <div className="flex flex-col gap-3">
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p
                          className="font-semibold break-all max-w-full"
                          style={primaryTextStyle}
                        >
                          {message.subject}
                        </p>
                        {message.readAt ? (
                          <Badge variant="secondary">
                            {t("account.messagesRead")}
                          </Badge>
                        ) : (
                          <Badge>{t("account.messagesUnread")}</Badge>
                        )}
                        <Badge variant="outline">
                          {resolveTypeLabel(message.type)}
                        </Badge>
                      </div>
                      <p className="text-sm text-secondary whitespace-pre-wrap break-words max-w-full">
                        {message.message}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    {!message.readAt && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMarkRead(message.id)}
                      >
                        {t("account.messagesMarkRead")}
                      </Button>
                    )}
                    <div
                      className="ml-auto text-right text-xs"
                      style={secondaryTextStyle}
                    >
                      <div>
                        {t("account.messagesSender")}: {senderLabel}
                      </div>
                      <div>
                        {t("account.messagesSentAt")}:{" "}
                        {new Date(message.createdAt).toLocaleString(
                          dateLocale
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              aria-label={t("common.back")}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-secondary">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              aria-label={t("common.next")}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
}
